import { Link } from "react-router-dom";
import { ChevronRight, Flag, Fish, Gift, HandHeart, Calendar, MapPin, Phone, Mail, Heart, Award, Users, Shield } from "lucide-react";
import Hero from "../components/Hero";
import SectionHeading from "../components/SectionHeading";
import { activities, stats, timeline, testimonials, galleryImages, contacts, chapterInfo } from "../data/mock";

const iconMap = { Flag, Fish, Gift, HandHeart };

export default function Home() {
  return (
    <main>
      <Hero />

      {/* Marquee band */}
      <div className="bg-[#0A3161] text-[#FFFFFF] py-5 overflow-hidden border-y-4 border-[#B22234]">
        <div className="flex whitespace-nowrap marquee-track">
          {[...Array(2)].map((_, k) => (
            <div key={k} className="flex items-center gap-12 pr-12 shrink-0">
              {[
                "Serving Sumner County Since 1986",
                "501(c)19 Service Organization",
                "Never Again Will One Generation Abandon Another",
                "Honor · Service · Brotherhood",
                "Chapter 240  |  Sumner County, Tennessee",
                "Freedom Ain't Free",
              ].map((t, i) => (
                <div key={i} className="flex items-center gap-12">
                  <span className="font-condensed uppercase tracking-[0.35em] text-sm">{t}</span>
                  <span className="text-[#B22234]">✦</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Mission */}
      <section className="relative py-28 px-6 lg:px-10">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <SectionHeading
              eyebrow="Our Charter"
              title="A Promise Kept Since 1986."
              description="Chartered on November 24, 1986, Chapter 240 of Vietnam Veterans of America stands as a 501(c)19 service organization dedicated to supporting the veterans and families of Sumner County, Tennessee."
            />
            <p className="mt-6 text-[#1F2937] leading-relaxed">
              Alongside the Chapter, we also maintain the Vietnam Veterans of Sumner County Foundation, a 501(c)3 that funnels donations directly into programs that make daily life better for those who served.
            </p>
            <div className="mt-10 flex gap-4">
              <Link
                to="/membership"
                className="inline-flex items-center gap-2 bg-[#0A3161] hover:bg-[#0A3161]/90 text-white px-7 h-12 font-condensed uppercase tracking-widest text-xs"
              >
                Become A Member <ChevronRight className="w-4 h-4" />
              </Link>
              <Link
                to="/veterans-park"
                className="inline-flex items-center gap-2 border border-[#0A3161] text-[#0A3161] hover:bg-[#0A3161] hover:text-white px-7 h-12 font-condensed uppercase tracking-widest text-xs transition-colors"
              >
                Veterans Park
              </Link>
            </div>
          </div>

          <div className="lg:col-span-7 relative">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-4 pt-12">
                <img
                  src="https://images.unsplash.com/photo-1621534864185-19a06671ed23"
                  alt="Folded flag"
                  className="w-full h-72 object-cover"
                />
                <img
                  src="https://images.unsplash.com/photo-1585502094744-8943e25971fa"
                  alt="Vietnam Veterans Memorial"
                  className="w-full h-56 object-cover"
                />
              </div>
              <div className="space-y-4">
                <img
                  src="https://images.unsplash.com/photo-1780668117245-af606acd76d9"
                  alt="Honor guard"
                  className="w-full h-56 object-cover"
                />
                <img
                  src="https://images.unsplash.com/photo-1658845543013-c1d3d03d37af"
                  alt="Vietnam memorial"
                  className="w-full h-72 object-cover"
                />
              </div>
            </div>
            {/* Motto badge */}
            <div className="absolute -bottom-8 -left-4 md:left-8 bg-[#B22234] text-white p-6 md:p-8 max-w-xs shadow-2xl">
              <p className="font-display italic text-lg md:text-xl leading-snug">
                &ldquo;Never again will one generation of veterans abandon another.&rdquo;
              </p>
              <p className="mt-3 font-condensed uppercase tracking-widest text-xs text-[#FFFFFF]">
                VVA National Motto
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-[#0A3161] relative overflow-hidden py-24">
        <div className="absolute inset-0 stars opacity-20" />
        <div className="absolute inset-0 grain opacity-30" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid md:grid-cols-4 gap-8">
            {stats.map((s, i) => (
              <div key={i} className="text-center md:text-left border-t border-[#FFFFFF]/30 pt-6">
                <p className="font-display text-5xl md:text-6xl font-bold text-[#FFFFFF] tabular">
                  {s.number}
                </p>
                <p className="mt-3 font-condensed uppercase tracking-widest text-white text-sm">
                  {s.label}
                </p>
                <p className="mt-1 text-[#F5F5F7]/60 text-sm">{s.detail}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Activities */}
      <section className="py-28 px-6 lg:px-10">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-16">
            <SectionHeading
              eyebrow="How We Serve"
              title="Programs & Events"
              description="From annual tournaments that fund veteran services to weekly food-bank hours, our chapter is where the mission becomes tangible."
            />
            <Link
              to="/activities"
              className="inline-flex items-center gap-2 font-condensed uppercase tracking-widest text-xs text-[#B22234] hover:gap-4 transition-all"
            >
              View All Activities <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            {activities.map((a, i) => {
              const Icon = iconMap[a.icon] || Flag;
              return (
                <article
                  key={a.id}
                  className="card-lift group relative overflow-hidden bg-white border border-[#D9DDE3]"
                >
                  <div className="relative h-72 overflow-hidden">
                    <img
                      src={a.image}
                      alt={a.title}
                      className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0A3161] via-[#0A3161]/30 to-transparent" />
                    <div className="absolute top-5 left-5">
                      <div className="w-12 h-12 bg-[#B22234] flex items-center justify-center">
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                    </div>
                    <div className="absolute bottom-5 left-5 right-5">
                      <p className="font-condensed uppercase tracking-widest text-sm font-semibold text-white drop-shadow">
                        {a.tagline}
                      </p>
                      <h3 className="mt-1 font-display text-white text-2xl md:text-3xl font-bold leading-tight">
                        {a.title}
                      </h3>
                    </div>
                  </div>
                  <div className="p-7">
                    <p className="text-[#1F2937] leading-relaxed">{a.description}</p>
                    <div className="mt-6 flex flex-wrap gap-4 text-sm">
                      <div className="flex items-center gap-2 text-[#0A3161]">
                        <Calendar className="w-4 h-4 text-[#B22234]" />
                        <span className="font-condensed uppercase tracking-wide text-xs">{a.date}</span>
                      </div>
                      <div className="flex items-center gap-2 text-[#0A3161]">
                        <MapPin className="w-4 h-4 text-[#B22234]" />
                        <span className="font-condensed uppercase tracking-wide text-xs">
                          {a.location.split(",")[0]}
                        </span>
                      </div>
                    </div>
                    {(a.id === "golf" || a.id === "fishing") && (
                      <Link
                        to={a.link}
                        className="mt-6 inline-flex items-center gap-2 font-condensed uppercase tracking-widest text-xs text-[#B22234] group-hover:gap-4 transition-all"
                      >
                        Learn More <ChevronRight className="w-4 h-4" />
                      </Link>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="relative py-28 px-6 lg:px-10 bg-[#EEF1F6]">
        <div className="max-w-7xl mx-auto">
          <SectionHeading
            align="center"
            eyebrow="Our Story"
            title="Four Decades of Service"
            description="From a handful of Vietnam veterans in 1986 to a thriving community organization today."
          />

          <div className="relative mt-16">
            <div className="absolute left-1/2 top-0 bottom-0 w-px bg-[#0A3161]/25 hidden md:block" />
            <div className="space-y-10 md:space-y-4">
              {timeline.map((t, i) => (
                <div
                  key={i}
                  className={`relative md:grid md:grid-cols-2 md:gap-16 items-start ${
                    i % 2 === 0 ? "" : "md:[direction:rtl]"
                  }`}
                >
                  <div className={`md:[direction:ltr] py-6 ${i % 2 === 0 ? "md:text-right md:pr-20" : "md:pl-20"}`}>
                    <p className="font-display text-5xl md:text-6xl font-bold text-[#B22234] leading-none">
                      {t.year}
                    </p>
                    <h3 className="mt-3 font-display text-2xl md:text-3xl font-bold text-[#0A3161]">
                      {t.title}
                    </h3>
                    <p className={`mt-4 text-[#1F2937] leading-relaxed max-w-md ${i % 2 === 0 ? "md:ml-auto" : ""}`}>
                      {t.description}
                    </p>
                  </div>
                  <div className="hidden md:flex md:[direction:ltr] justify-center pt-8">
                    <div className="relative">
                      <div className="w-5 h-5 rounded-full bg-[#B22234] border-4 border-[#EEF1F6] shadow-md" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Values / Pillars */}
      <section className="py-28 px-6 lg:px-10">
        <div className="max-w-7xl mx-auto">
          <SectionHeading
            align="center"
            eyebrow="What We Stand For"
            title="Four Pillars Of Chapter 240"
          />
          <div className="mt-20 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Shield, title: "Advocacy", text: "Fighting for the rights, benefits and recognition every veteran has earned." },
              { icon: Users, title: "Brotherhood", text: "A community that stands together, across generations of service." },
              { icon: Heart, title: "Compassion", text: "Feeding families, gifting children, and lifting neighbors when they need it most." },
              { icon: Award, title: "Honor", text: "Ceremonies, memorials and traditions that pay lasting tribute to those who served." },
            ].map((p, i) => (
              <div key={i} className="group relative bg-white border border-[#D9DDE3] p-8 card-lift">
                <div className="w-14 h-14 bg-[#0A3161] flex items-center justify-center mb-6 group-hover:bg-[#B22234] transition-colors">
                  <p.icon className="w-7 h-7 text-[#FFFFFF]" />
                </div>
                <h3 className="font-display text-2xl font-bold text-[#0A3161]">{p.title}</h3>
                <p className="mt-3 text-[#1F2937] leading-relaxed">{p.text}</p>
                <div className="absolute bottom-0 left-0 h-1 w-0 bg-[#B22234] group-hover:w-full transition-all duration-500" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Flag Ceremony CTA */}
      <section className="relative py-32 px-6 lg:px-10 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1773434035272-11ba3043c2e2"
            alt="Honor Guard"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A3161]/95 via-[#0A3161]/85 to-[#0A3161]/95" />
        </div>
        <div className="relative max-w-4xl mx-auto text-center">
          <SectionHeading
            align="center"
            light
            eyebrow="By Request"
            title="Flag Ceremony, Color Guard & Honor Guard"
            description="For funerals, memorials, civic events, and dedications. Our uniformed volunteers are honored to serve."
          />
          <div className="mt-12 inline-block bg-white text-[#0A3161] p-10 shadow-2xl border-t-4 border-[#B22234]">
            <p className="font-condensed uppercase tracking-[0.3em] text-xs text-[#B22234] font-semibold">
              Contact
            </p>
            <p className="mt-2 font-display text-3xl md:text-4xl text-[#0A3161] font-bold">{contacts.flagCeremony.name}</p>
            <div className="mt-6 flex flex-col md:flex-row gap-4 md:gap-8 justify-center text-[#0A3161]">
              <a href={`mailto:${contacts.flagCeremony.email}`} className="flex items-center gap-2 hover:text-[#B22234] font-semibold">
                <Mail className="w-5 h-5" /> {contacts.flagCeremony.email}
              </a>
              <a href={`tel:${contacts.flagCeremony.phone}`} className="flex items-center gap-2 hover:text-[#B22234] font-semibold">
                <Phone className="w-5 h-5" /> {contacts.flagCeremony.phone}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-28 px-6 lg:px-10 bg-[#EEF1F6]">
        <div className="max-w-7xl mx-auto">
          <SectionHeading
            align="center"
            eyebrow="Voices From The Community"
            title="Words We Carry With Us"
          />
          <div className="mt-16 grid md:grid-cols-3 gap-8">
            {testimonials.map((t, i) => (
              <div key={i} className="bg-white p-8 border border-[#D9DDE3] relative">
                <div className="absolute -top-4 left-8 w-10 h-10 bg-[#B22234] flex items-center justify-center">
                  <span className="font-display text-white text-3xl leading-none pt-1">&ldquo;</span>
                </div>
                <p className="mt-4 font-display italic text-lg text-[#0A3161] leading-relaxed">
                  {t.quote}
                </p>
                <div className="mt-6 pt-6 border-t border-[#D9DDE3]">
                  <p className="font-condensed uppercase tracking-widest text-xs text-[#B22234]">
                    {t.author}
                  </p>
                  <p className="mt-1 text-sm text-[#374151]">{t.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="py-28 px-6 lg:px-10">
        <div className="max-w-7xl mx-auto">
          <SectionHeading
            eyebrow="In The Field"
            title="Moments Of Service"
            description="Snapshots from our tournaments, memorials, and community events."
          />
          <div className="mt-16 grid grid-cols-2 md:grid-cols-3 gap-4">
            {galleryImages.map((img, i) => (
              <div
                key={i}
                className={`group relative overflow-hidden ${
                  i === 0 || i === 4 ? "md:row-span-2 md:h-[500px]" : "h-64"
                }`}
              >
                <img
                  src={img}
                  alt=""
                  className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-[#0A3161]/10 group-hover:bg-[#0A3161]/40 transition-colors" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Membership CTA */}
      <section className="relative py-32 px-6 lg:px-10 bg-[#B22234] overflow-hidden">
        <div className="absolute inset-0 stars opacity-20" />
        <div className="absolute inset-0 grain opacity-40" />
        <div className="relative max-w-5xl mx-auto text-center">
          <p className="font-condensed uppercase tracking-[0.35em] text-[#FFFFFF] text-xs mb-6">
            Answer The Call
          </p>
          <h2 className="font-display font-bold text-white text-5xl md:text-6xl lg:text-7xl leading-[1.05]">
            Stand With Us.
            <br />
            <span className="italic text-[#FFFFFF]">Every Veteran Matters.</span>
          </h2>
          <p className="mt-8 text-white/85 text-lg max-w-2xl mx-auto leading-relaxed">
            Membership fuels our tournaments, our food bank, and the memorials that keep our brothers and sisters remembered. Join Chapter 240 today.
          </p>
          <div className="mt-12 flex flex-wrap gap-4 justify-center">
            <Link
              to="/membership"
              className="inline-flex items-center gap-2 bg-white text-[#B22234] hover:bg-[#FFFFFF] hover:text-[#0A3161] px-8 h-14 font-condensed uppercase tracking-widest text-sm transition-colors"
            >
              Apply for Membership <ChevronRight className="w-4 h-4" />
            </Link>
            <a
              href={`tel:${chapterInfo.phone}`}
              className="inline-flex items-center gap-2 border border-white/40 hover:border-white text-white px-8 h-14 font-condensed uppercase tracking-widest text-sm"
            >
              <Phone className="w-4 h-4" /> Call {chapterInfo.phone}
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}
